import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { HeaderErrorEnum } from '../enums/header-error.enum';
import { SubUrlsEnum } from '../enums/sub-urls.enum';
import { IEntityDTOIn, IEntityFromBack } from '../interfaces/groups/entity.interface';
import { IGroup, IGroupDTOIn, IGroupDTOOut } from '../interfaces/groups/group.interface';
import { MappersService } from './mappers.service';

@Injectable({
    providedIn: 'root',
})
export class GroupsService {
    constructor(private http: HttpClient, private mapperService: MappersService) {}

    // READ
    public getAllGroups(studentId?: number): Observable<IGroup[]> {
        let params: HttpParams = new HttpParams();
        if (studentId) {
            params = params.set('viewing_student_id', studentId);
        }
        return this.http
            .get<IEntityFromBack>(environment.wasoApi + SubUrlsEnum.GROUPS, { params: studentId ? params : null })
            .pipe(
                map((result: IEntityFromBack) =>
                    result.groups.map((group: IEntityDTOIn) =>
                        this.mapperService.castIEntityToIGroup(this.mapperService.fromIEntityDTOInToIEntity(group))
                    )
                )
            );
    }

    public getGroupById(id: number): Observable<IGroup> {
        return this.http
            .get<IEntityDTOIn>(environment.wasoApi + SubUrlsEnum.GROUP + id)
            .pipe(
                map((result: IEntityDTOIn) => this.mapperService.castIEntityToIGroup(this.mapperService.fromIEntityDTOInToIEntity(result)))
            );
    }

    // CREATE
    public createGroup(newGroup: IGroupDTOOut): Observable<IGroup> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .post<IGroupDTOIn>(environment.wasoApi + SubUrlsEnum.GROUPS, newGroup, { headers })
            .pipe(
                map((group: IEntityDTOIn) => this.mapperService.castIEntityToIGroup(this.mapperService.fromIEntityDTOInToIEntity(group)))
            );
    }

    // UPDATE
    public patchGroup(group: IGroupDTOOut, id: number): Observable<IGroup> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .patch<IGroupDTOIn>(environment.wasoApi + SubUrlsEnum.GROUPS + '/' + id, group, { headers })
            .pipe(
                map((entity: IGroupDTOIn) => this.mapperService.castIEntityToIGroup(this.mapperService.fromIEntityDTOInToIEntity(entity)))
            );
    }

    // DELETE
    public deleteGroup(id: number): Observable<void> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.GROUPS + '/' + id, {
            headers,
        });
    }

    // cruD: DELETE -> ENABLE / DISABLE
    public enableGroup(groupId: number): Observable<IGroup> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        const fullUrl: string = environment.wasoApi + SubUrlsEnum.GROUPS + '/' + groupId + '/' + SubUrlsEnum.STATUS;
        return this.http
            .post<IEntityDTOIn>(fullUrl, {}, { headers })
            .pipe(
                map((entity: IEntityDTOIn) => this.mapperService.castIEntityToIGroup(this.mapperService.fromIEntityDTOInToIEntity(entity)))
            );
    }

    public disableGroup(groupId: number): Observable<IGroup> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        const fullUrl: string = environment.wasoApi + SubUrlsEnum.GROUPS + '/' + groupId + '/' + SubUrlsEnum.STATUS;
        return this.http
            .delete<IEntityDTOIn>(fullUrl, { headers })
            .pipe(
                map((entity: IEntityDTOIn) => this.mapperService.castIEntityToIGroup(this.mapperService.fromIEntityDTOInToIEntity(entity)))
            );
    }
}
